'use client';
import { useState, useCallback } from 'react';
import {
  getHomeFeed,
  isNetworkAvailable,
  useProfileStore,
  useCropPreferenceStore,
  useAppPersistenceStore,
  KissanEventType
} from '@ninjacart/nc-nk-store';
import {
  useQueuedEventStore,
  addEventParam,
  useEventStore,
  
} from '@ninjacart/nc-events-sdk-v2';
import { IAdaptiveActionCard } from '@ninjacart/nc-nk-core';
import { IHomeFeedResponseType } from '../../types/homeTypes';
import { crashlytics } from '@ninjacart/nc-nk-firebase';

export function useFeedApi() {
  const [loading, setLoading] = useState(false);
  const userProfile = useProfileStore((state) => state.userProfile);
  const cropPreference = useCropPreferenceStore((state) => state.cropPreference);
  const cropLangaugeByName = useCropPreferenceStore((state) => state.cropLangaugeByName);
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
  const latitude = useProfileStore((state) => state.latitude);
  const longitude = useProfileStore((state) => state.longitude);
  const userPreferenceId = useCropPreferenceStore((state) => state.userPreferenceId);
  const referInfo = useAppPersistenceStore((state) => state.referInfo);
  const eventDetails = useEventStore((state) => state.eventDetails);
  const enqueue = useQueuedEventStore((state) => state.enqueue);

  const getHomeFeeds = useCallback(
    async (payloadRef): Promise<IHomeFeedResponseType | null> => {
      if (loading) return null;

      setLoading(true);
      try {
        // Check network availability
        if (!isNetworkAvailable()) {
          throw new Error('No internet available!!!');
        }

        const utmSource = eventDetails?.manualSource ?? '';
        const utmMedium = eventDetails?.manualMedium ?? '';
        const utmCampaign = eventDetails?.manualCampaignName ?? '';

        // Build payload for API request
        const payload = {
          offset: payloadRef.offset,
          count: payloadRef.count,
          next: payloadRef.next,
          refresh: payloadRef.refresh,
          isUserPersonalized: true,
          hasWeather: true,
          hasCrop: true,
          hasAnimal: true,
          hasLogin: true,
          hasLanguage: true,
          hasPlaylist: true,
          hasMyCourseList: true,
          hasProducts: true,
          shorts: 'v3',
          isShortUserPersonalized: true,
          ...(uniqueDeviceId && { deviceId: uniqueDeviceId }),
          ...(userProfile?.user?.id && { userId: userProfile.user.id }),
          ...(latitude && { geoLatitude: latitude }),
          ...(longitude && { geoLongitude: longitude }),
          ...(userPreferenceId && { userPreferenceId: userPreferenceId }),
          ...(userProfile?.realm?.id && { realmId: userProfile.realm.id }),
          ...(referInfo?.installReferrer && { installRefferal: referInfo?.installReferrer }),
          ...(utmSource && { utmSource }),
          ...(utmMedium && { utmMedium }),
          ...(utmCampaign && { utmCampaign }),
        };

        // Prepare crop parameters
        const cropIdParams: string[] = !cropPreference?.length
          ? []
          : cropPreference.map(
              (crop) =>
                `${encodeURIComponent(crop?.type.toLowerCase())}:${encodeURIComponent(crop?.domainId!)}`
            );

        const cropNameParams: string[] = !cropLangaugeByName?.length
          ? []
          : cropLangaugeByName.map((crop) => `${encodeURIComponent(crop)}`);

        // Call API
        const homeFeeds = await getHomeFeed(payload, cropIdParams, cropNameParams);

        if (!homeFeeds?.data?.length) {
          // Log event for no data found
          const noDataEvent = {
            name: KissanEventType.HOMEPAGE_NO_DATA_FOUND,
            screenDetails: { screen: 'feed page' },
            eventParams: [
              addEventParam('offset', payloadRef.offset),
              addEventParam('count', payloadRef.count),
            ],
          };
          enqueue(noDataEvent);
          return null;
        }

        // Transform API response
        const transformedData: IAdaptiveActionCard[] = homeFeeds?.data.map((item) => {
          const revisedItem = item?.actionData;
          return revisedItem.mediaDetails?.length === 1
            ? { ...item, cardType: 'media' }
            : { ...item, cardType: 'multiMedia' };
        });

        return {
          data: transformedData,
          offset: homeFeeds.offset,
          next: homeFeeds.next,
        };
      } catch (error) {
        // Handle and log errors
        if (error.toString() !== 'No internet available!!!') {
          const failedEvent = {
            name: KissanEventType.HOMEPAGE_LOAD_FAILED,
            screenDetails: { screen: 'feed page' },
            eventParams: [addEventParam('errorMessage', error.toString())],
          };
          enqueue(failedEvent);
          crashlytics()?.recordError(error, 'HOME_FEED_API_ERROR');
        }
        console.error('API Error:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      userProfile,
      cropPreference,
      cropLangaugeByName,
      uniqueDeviceId,
      latitude,
      longitude,
      userPreferenceId,
      referInfo,
      eventDetails
    ]
  );

  return {
    getHomeFeeds,
    loading,
  };
}
